import { ExpressionRuleEditor } from "../ExpressionRuleEditor";
import { DataDefinition } from "../../../rule-engine-core/Common/Data/DataDefinition";
import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { ExpressionPiece } from "../../Builder/ExpressionStyles";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import React, { useContext, useState } from "react";
import { ExistsExpression } from "../../../rule-engine-core/Expressions/Expressions/Logic/Exists";
import styled from "styled-components";
import { ValidationIssue, ValidationLevel } from "../../../rule-engine-core/Common/IDesignValidationProvider";
import VariablePicker, { IVariablePicker } from "../../../CommonUI/VariablePicker/VariablePicker";
import { ExpressionReactContext, ExpressionRenderType } from "../../Structure/ExpressionReactContext";
import { AttentionIcon } from "../../../CommonUI/Icon/AttentionIcon";
import { VariableElement } from "../../Builder/ExpressionValueRenderer";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";

export class ExistsExpressionEditor extends ExpressionRuleEditor<ExistsExpression> {
    render() {
        let dataDef = this.ruleData!.expressionContext?.definition?.getInputDefinition() || new DataDefinition();

        return (
            <ObserveState
                listenTo={this.ruleData}
                control={() => {
                    let validationIssues = this.ruleData!.getValidationIssues();
                    return (
                        <ExpressionPiece>
                            {dataDef && (
                                <ObserveState
                                    listenTo={dataDef}
                                    control={() => {
                                        return (
                                            <ExistsRender
                                                selected={this.ruleData!.dataPath}
                                                data={dataDef!}
                                                allowNewVariable={
                                                    this.ruleData!.expressionContext?.definition?.canChangeInputs
                                                }
                                                onVariableSet={(path: string, def?: IDataDefinition) => {
                                                    if (def) {
                                                        dataDef!.fields.push(def);
                                                        this.ruleData!.dataPath = def.name;
                                                    } else {
                                                        this.ruleData!.dataPath = path;
                                                    }

                                                    StateManager.propertyChanged(this, "dataPath");
                                                }}
                                                issues={validationIssues}
                                            />
                                        );
                                    }}
                                />
                            )}
                            &nbsp; exists
                            {validationIssues.length > 0 && (
                                <>
                                    <ErrorMessageRender
                                        className="ExpressionValidation-errorMessage"
                                        type={validationIssues[0].level}
                                    >
                                        {validationIssues[0].issue}
                                    </ErrorMessageRender>
                                </>
                            )}
                        </ExpressionPiece>
                    );
                }}
            />
        );
    }
}

const VariablePickerWrapper = styled.div<{ error: boolean }>`
    padding: 5px;

    position: relative;

    border: solid 2px transparent;

    border-radius: 8px;

    ${(props) =>
        props.error
            ? `
                
                .ExpressionValidation-errorMessage {
                    opacity: 0;
                    z-index: -1;
                }

                &:hover {
                    border: solid 2px ${UIStyleConstants.RED_COLOR};
                    .ExpressionValidation-errorMessage {
                        opacity: 1;
                        z-index: 1;
                    }
                }
            `
            : ``}
`;

const ErrorMessageRender = styled.div<{ type: ValidationLevel }>`
    font-size: 8px;
    font-style: normal;
    font-weight: 500;
    line-height: 10px;
    letter-spacing: 0.1px;
    text-align: left;

    white-space: pre;

    z-index: 2;

    position: absolute;
    top: calc(100% + 3px);
    left: 0;
    color: ${(props) =>
        props.type === ValidationLevel.ERROR ? UIStyleConstants.RED_COLOR : UIStyleConstants.ORANGE_COLOR};
`;

const ValidationErrorRender = styled.div`
    position: absolute;
    top: -5px;
    right: 0px;
`;

const VariableRender = styled.div`
    font-size: 14px;
    line-height: 24px;
    border-bottom: dotted 1px ${UIStyleConstants.GRAY_COLOR};
    font-style: italic;
    display: block;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

    border-radius: 4px;

    width: 100%;
    padding: 0 5px;
`;

interface ExistsRenderProps extends IVariablePicker {
    issues: ValidationIssue[];
}

const ExistsRender = ({ issues, ...props }: ExistsRenderProps) => {
    const [open, setOpen] = useState<boolean>(false);

    const context = useContext(ExpressionReactContext);

    if (context.renderType === ExpressionRenderType.FULL) {
        return (
            <VariablePickerWrapper error={issues.length > 0}>
                <VariablePicker
                    {...props}
                    open={open}
                    onClose={() => setOpen(false)}
                    renderInput={() => (
                        <VariableRender
                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                event.stopPropagation();
                                event.preventDefault();
                                setOpen(true);
                            }}
                        >
                            {props.selected}
                            {issues.length > 0 && (
                                <ValidationErrorRender>
                                    <AttentionIcon />
                                </ValidationErrorRender>
                            )}
                        </VariableRender>
                    )}
                />
            </VariablePickerWrapper>
        );
    }

    return (
        <VariableElement
            renderType={context.renderType}
            compactColor={context.color}
        >
            <i>{props.selected}</i>
        </VariableElement>
    );
};
